import React, { FC, useCallback, useContext, useState } from "react";
import { BubbleMenu, Editor } from "@tiptap/react";
import { TiptapEditorContext } from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import EditLinkDialog from "@src/features/libraryItem/course/EditLinkDialog";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import TextBoldIcon from "@src/lib/icons/18px/text-bold";
import TextItalicIcon from "@src/lib/icons/18px/text-italic";
import UnorderedListIcon from "@src/lib/icons/18px/unordered-list";
import OrderedListIcon from "@src/lib/icons/18px/ordered-list";
import Table3RowsOutlineIcon from "@src/lib/icons/18px/table-3-rows-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import EmojiPicker from "@src/lib/ui/EmojiPicker";
import { doesPromptScreenContainTable } from "@src/features/libraryItem/course/utils/tipTapTable";
import { cn } from "@src/lib/utils/cn";

type Props = {
  editor: Editor;
};

/**
 * The white selection pill as it was before the glass canvas tools. Shown
 * while Ff.LibraryItemOverviewTab is off (useBuilderCanvasTools); delete it
 * with the flag.
 */
const LegacyFormattingBubbleMenu: FC<Props> = ({ editor }) => {
  const tiptapEditorContext = useContext(TiptapEditorContext);
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [addLinkText, setAddLinkText] = useState("");
  const [addLinkInitialLink, setAddLinkInitialLink] = useState("");
  const [addLinkSelection, setAddLinkSelection] = useState<{
    from: number;
    to: number;
  } | null>(null);

  const promptScreenContainsTable = React.useMemo(() => {
    const editorState = editor?.state;
    if (editorState) {
      return doesPromptScreenContainTable(editorState);
    }
    return false;
  }, [editor?.state]);

  const tableDisabled =
    promptScreenContainsTable && !tiptapEditorContext?.formatting.isTable;

  const onEmojiSelect = useCallback(
    (emoji: string) => {
      if (editor) {
        const insertPoint = editor.view?.state?.selection?.anchor || 1;
        editor
          .chain()
          .focus()
          .insertContentAt(insertPoint, emoji, {
            parseOptions: {
              preserveWhitespace: false,
            },
          })
          .run();
      }
    },
    [editor],
  );

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 10,
        interactive: true,
        placement: "top",
      }}
      className="flex h-10 items-center gap-0 rounded-full bg-bg-elevated p-1 shadow-large"
      shouldShow={({ editor, state }) => {
        // Only show when there's a text selection (not just cursor position)
        const { from, to } = state.selection;
        const hasSelection = from !== to;

        // Don't show if selection contains only a table
        const isTableSelected = editor.isActive("table");

        return hasSelection && !isTableSelected;
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(editor.isActive("bold") && "bg-state-pressed")}
              onClick={() => {
                editor.chain().focus().toggleBold().run();
              }}
            >
              <TextBoldIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Bold</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(editor.isActive("italic") && "bg-state-pressed")}
              onClick={() => {
                editor.chain().focus().toggleItalic().run();
              }}
            >
              <TextItalicIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Italic</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(
                editor.isActive("bulletList") && "bg-state-pressed",
              )}
              onClick={() => {
                editor.chain().focus().toggleBulletList().run();
              }}
            >
              <UnorderedListIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Bullet List</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(
                editor.isActive("orderedList") && "bg-state-pressed",
              )}
              onClick={() => {
                editor.chain().focus().toggleOrderedList().run();
              }}
            >
              <OrderedListIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Numbered List</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(editor.isActive("table") && "bg-state-pressed")}
              disabled={tableDisabled}
              onClick={() => {
                if (tiptapEditorContext?.formatting.isTable) {
                  return;
                }
                const startPos = editor.state.selection.$anchor.pos || 0;
                editor
                  .chain()
                  .focus()
                  .insertContent(
                    "<p></p><table><tr><td><p></p></td><td><p></p></td></tr><tr><td><p></p></td><td><p></p></td></tr></table><p></p>",
                  )
                  .setTextSelection(startPos + 4)
                  .run();
              }}
            >
              <Table3RowsOutlineIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {tableDisabled ? "One table per step" : "Table"}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              className={cn(editor.isActive("link") && "bg-state-pressed")}
              onClick={() => {
                const selection = editor.view.state.selection;
                if (!selection) return;
                const link = editor.getAttributes("link").href;
                const text = editor.view.state.doc.textBetween(
                  selection.from,
                  selection.to,
                  " ",
                );
                setAddLinkText(text ?? "");
                setAddLinkInitialLink(link ?? "");
                setAddLinkSelection({
                  from: selection.from,
                  to: selection.to,
                });
                setAddLinkOpen(true);
              }}
            >
              <LinkIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Link</TooltipContent>
        </Tooltip>

        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon-md">
                  <EmojiOutlineIcon />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Emoji</TooltipContent>
          </Tooltip>
          <PopoverContent
            side="top"
            align="center"
            className="w-auto p-0"
            sideOffset={10}
          >
            <EmojiPicker onEmojiSelect={onEmojiSelect} />
          </PopoverContent>
        </Popover>
      </TooltipProvider>
      {addLinkOpen && (
        <EditLinkDialog
          open={addLinkOpen}
          onOpenChange={setAddLinkOpen}
          text={addLinkText}
          initialLink={addLinkInitialLink}
          onComplete={(text, link) => {
            if (!addLinkSelection) return;
            editor
              .chain()
              .focus()
              .setLink({ href: link, target: "_blank" })
              .insertContentAt(
                {
                  from: addLinkSelection.from,
                  to: addLinkSelection.to,
                },
                text,
              )
              .unsetMark("link")
              .focus()
              .run();
          }}
        />
      )}
    </BubbleMenu>
  );
};

export default LegacyFormattingBubbleMenu;
